import React, { useEffect, useMemo, useRef, useState } from "react";
import Text from "../../../common/typography/text.js";
import Button from "../../../common/button/button.js";
import Panel from "../../../common/panel/index.js";
import DrillLevelTable from "./DrillLevelTable.js";
import PortfolioDonut from "./PortfolioDonut.js";
import KeyDrivers from "./KeyDrivers.js";
import { fetchDrillDown } from "../aabiService";
import { buildPortfolioRows, computeKeyDrivers, selectedMetricKey } from "../aabiViewModel";
import { LEVELS, CHILD_LEVEL, LEVEL_LABELS, MEASURE_LABELS } from "../aabiConstants";
import "./../AABI.css";

// The donut cascade hierarchy: Product Category → Product Group → Product
// Code. The first slot's level depends on the page scope; deeper slots wake
// up as sections are selected in the slot before them, each drawing its own
// donut once it has data.
const SLOT_LEVELS = [LEVELS.CATEGORY, LEVELS.GROUP, LEVELS.PRODUCT];

const START_INDEX = {
  [LEVELS.MANUFACTURER]: 0,
  [LEVELS.CATEGORY]: 1,
  [LEVELS.GROUP]: 2,
  [LEVELS.PRODUCT]: 2,
};

const PENDING_HINTS = {
  [LEVELS.CATEGORY]: "Select a section to see its product categories.",
  [LEVELS.GROUP]: "Select a product category to see its product groups.",
  [LEVELS.PRODUCT]: "Select a product group to see its product codes.",
};

// The drill explorer (§5.1 "Portfolio performance"): a donut cascade over the
// hierarchy plus a Data view that mirrors whichever level is active. Chart/Data
// and the cascade selections are local UI state — they refine the analysis
// without rescoping the page, so they never enter the shareable URL.
const PortfolioExplorer = ({
  manufacturerId,
  scope,
  rows,
  totals,
  measure,
  level,
  period,
  loading,
  error,
  onRetry,
  onInvoiceRow,
  scopeSummary,
}) => {
  const [view, setView] = useState("chart");
  const [selected, setSelected] = useState({ [LEVELS.CATEGORY]: null, [LEVELS.GROUP]: null });
  const [childData, setChildData] = useState({ [LEVELS.GROUP]: null, [LEVELS.PRODUCT]: null });
  const [fetchState, setFetchState] = useState({ key: null, loading: false, error: false });
  const [retryNonce, setRetryNonce] = useState(0);
  const requestIdRef = useRef(0);
  const cacheRef = useRef(new Map());

  const start = START_INDEX[level] ?? 0;
  const isProductScope = level === LEVELS.PRODUCT;
  const metricKey = selectedMetricKey(measure);

  const portfolioRows = useMemo(
    () => buildPortfolioRows(rows, totals, measure),
    [rows, totals, measure],
  );

  const drivers = useMemo(() => computeKeyDrivers(portfolioRows), [portfolioRows]);

  // Canonical hierarchy chain: the page scope first, then local cascade
  // selections on top of it.
  const chainCategoryId = scope?.categoryId ?? selected[LEVELS.CATEGORY]?.id ?? null;
  const chainGroupId = scope?.groupId ?? selected[LEVELS.GROUP]?.id ?? null;

  const deepestSelected = selected[LEVELS.GROUP]
    ? LEVELS.GROUP
    : selected[LEVELS.CATEGORY]
      ? LEVELS.CATEGORY
      : null;

  const fetchKey = deepestSelected
    ? [
        deepestSelected,
        chainCategoryId,
        chainGroupId,
        period?.startDate,
        period?.endDate,
      ].join("|")
    : null;

  // A page scope/period change invalidates the whole cascade.
  const scopeKey = [
    level,
    scope?.categoryId,
    scope?.groupId,
    scope?.productId,
    period?.startDate,
    period?.endDate,
  ].join("|");

  useEffect(() => {
    setSelected({ [LEVELS.CATEGORY]: null, [LEVELS.GROUP]: null });
    setChildData({ [LEVELS.GROUP]: null, [LEVELS.PRODUCT]: null });
    setFetchState({ key: null, loading: false, error: false });
    cacheRef.current.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  // Fetch the child rows for the deepest selection. Cached per
  // scope/period/selection — a measure toggle never refetches because the
  // drill payload already carries both measures (§8.6).
  useEffect(() => {
    if (!fetchKey) return;
    const targetLevel = CHILD_LEVEL[deepestSelected];
    const cached = cacheRef.current.get(fetchKey);
    if (cached) {
      setChildData((prev) => ({ ...prev, [targetLevel]: cached }));
      setFetchState({ key: fetchKey, loading: false, error: false });
      return;
    }

    const requestId = ++requestIdRef.current;
    setFetchState({ key: fetchKey, loading: true, error: false });
    fetchDrillDown({
      manufacturerId,
      level: deepestSelected,
      categoryId: chainCategoryId,
      groupId: chainGroupId,
      startDate: period?.startDate,
      endDate: period?.endDate,
      compareStartDate: period?.compareStartDate,
      compareEndDate: period?.compareEndDate,
    })
      .then((response) => {
        if (requestId !== requestIdRef.current) return;
        const payload = {
          rows: response?.rows ?? [],
          totals: response?.totals ?? null,
        };
        cacheRef.current.set(fetchKey, payload);
        setChildData((prev) => ({ ...prev, [targetLevel]: payload }));
        setFetchState({ key: fetchKey, loading: false, error: false });
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        console.error("Failed to fetch AABI portfolio breakdown", err);
        setFetchState({ key: fetchKey, loading: false, error: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey, retryNonce]);

  const handleSelect = (slotIndex, row) => {
    const slotLevel = SLOT_LEVELS[start + slotIndex];
    // null-id rows ("Uncategorized" / "Ungrouped") have no child scope to open.
    if (!slotLevel || row?.id == null) return;
    setSelected((prev) => {
      const isSame = prev[slotLevel]?.id === row.id;
      const next = { ...prev, [slotLevel]: isSame ? null : row };
      // Selecting (or toggling off) a section resets everything deeper.
      SLOT_LEVELS.slice(SLOT_LEVELS.indexOf(slotLevel) + 1).forEach((deeper) => {
        next[deeper] = null;
      });
      return next;
    });
  };

  const retryChildFetch = () => {
    if (fetchKey) cacheRef.current.delete(fetchKey);
    setRetryNonce((nonce) => nonce + 1);
  };

  // Build the visible slots: the first is fed by the page scope's rows; each
  // next one is pending until a section is selected in its predecessor.
  const slots = useMemo(() => {
    const built = [];
    for (let i = 0; start + i < SLOT_LEVELS.length; i += 1) {
      const slotLevel = SLOT_LEVELS[start + i];
      const parentLevel = i === 0 ? null : SLOT_LEVELS[start + i - 1];
      const parentSelection = i === 0 ? true : Boolean(selected[parentLevel]);

      if (!parentSelection) {
        built.push({ level: slotLevel, status: "pending", rows: [], totalValue: 0, activeId: null, subtitle: null });
        continue;
      }

      const subtitle = parentLevel ? selected[parentLevel]?.name ?? null : null;

      if (i === 0) {
        built.push({
          level: slotLevel,
          status: loading ? "loading" : "ready",
          rows: portfolioRows,
          totalValue: totals?.[metricKey] ?? 0,
          activeId: selected[slotLevel]?.id ?? null,
          subtitle,
        });
        continue;
      }

      const payload = childData[slotLevel];
      const slotRows = payload
        ? buildPortfolioRows(payload.rows, payload.totals, measure)
        : [];
      const targetsThisSlot = fetchState.key ? deepestSelected === parentLevel : false;
      const status = targetsThisSlot && fetchState.loading
        ? "loading"
        : targetsThisSlot && fetchState.error
          ? "error"
          : "ready";

      built.push({
        level: slotLevel,
        status,
        rows: slotRows,
        totalValue: payload?.totals?.[metricKey] ?? 0,
        activeId: selected[slotLevel]?.id ?? null,
        subtitle,
      });
    }
    return built;
  }, [start, selected, childData, portfolioRows, totals, measure, metricKey, loading, fetchState, deepestSelected]);

  // The Data view mirrors the deepest active slot of the cascade.
  const activeSlotIndex = slots.reduce(
    (deepest, slot, index) => (slot.status !== "pending" ? index : deepest),
    0,
  );
  const activeSlot = slots[activeSlotIndex] ?? slots[0];

  if (error) {
    return (
      <Panel title="Portfolio performance" aria-label="Portfolio performance">
        <div role="alert" className="aabi-panel-error">
          <Text variant="smallText" color="failureColor">
            Couldn&apos;t load the portfolio breakdown.
          </Text>
          {onRetry && (
            <Button variant="primary" onClick={onRetry}>
              Retry
            </Button>
          )}
        </div>
      </Panel>
    );
  }

  const measureLabel = MEASURE_LABELS[measure] || "Revenue";

  if (isProductScope) {
    return (
      <Panel
        title="Portfolio performance"
        aria-label="Portfolio performance"
        aria-busy={loading}
      >
        <div className="aabi-product-summary">
          {scopeSummary ? (
            <Text variant="smallText" color="lightText">
              {scopeSummary}
            </Text>
          ) : null}
          {portfolioRows.length === 0 && !loading && (
            <Text variant="smallText" color="lightText">
              No invoice activity for this product in the selected period.
            </Text>
          )}
        </div>
      </Panel>
    );
  }

  return (
    <Panel
      title="Portfolio performance"
      aria-label="Portfolio performance"
      aria-busy={loading}
      contentClassName="aabi-portfolio-content"
      actions={
        <>
          <Button
            variant="timeframe"
            isActive={view === "chart"}
            onClick={() => setView("chart")}
          >
            Chart
          </Button>
          <Button
            variant="timeframe"
            isActive={view === "data"}
            onClick={() => setView("data")}
          >
            Data
          </Button>
        </>
      }
    >

      {!loading && portfolioRows.length === 0 ? (
        <div className="aabi-empty-state">
          <Text variant="smallText" color="lightText">
            No sales activity for this period.
          </Text>
        </div>
      ) : view === "chart" ? (
        <div className="aabi-donut-row">
          {slots.map((slot, index) => (
            <PortfolioDonut
              key={slot.level}
              title={LEVEL_LABELS[slot.level]}
              subtitle={slot.subtitle}
              portfolioRows={slot.rows}
              totalValue={slot.totalValue}
              measure={measure}
              activeId={slot.activeId}
              onSelect={(row) => handleSelect(index, row)}
              onInvoiceRow={onInvoiceRow}
              status={slot.status}
              pendingHint={PENDING_HINTS[slot.level]}
              onRetry={retryChildFetch}
            />
          ))}
        </div>
      ) : (
        <div className="aabi-data-view">
          <div className="aabi-donut-slot-header">
            <Text variant="smallText" color="darkText">
              {LEVEL_LABELS[activeSlot?.level]}
            </Text>
            {activeSlot?.subtitle && (
              <Text variant="tinyText" color="lightText">
                {activeSlot.subtitle}
              </Text>
            )}
          </div>
          {activeSlot?.status === "error" ? (
            <div role="alert" className="aabi-panel-error">
              <Text variant="smallText" color="failureColor">
                Couldn&apos;t load the portfolio breakdown.
              </Text>
              <Button variant="primary" onClick={retryChildFetch}>
                Retry
              </Button>
            </div>
          ) : (
            <DrillLevelTable
              rows={activeSlot?.rows ?? []}
              measure={measure}
              loading={loading || activeSlot?.status === "loading"}
              onRowClicked={(row) => {
                if (row?.id == null) return;
                if (row?.isLeaf) {
                  onInvoiceRow?.(row);
                  return;
                }
                handleSelect(activeSlotIndex, row);
              }}
            />
          )}
        </div>
      )}

      {!loading && portfolioRows.length > 0 && <KeyDrivers drivers={drivers} />}

      {view === "chart" && !loading && portfolioRows.length > 0 && (
        <Text variant="tinyText" color="lightText">
          Ranked by {measureLabel.toLowerCase()}; share is of each level&apos;s
          total. Select a section to open the next level of the hierarchy.
        </Text>
      )}
    </Panel>
  );
};

export default PortfolioExplorer;
